import { useState, useRef, type ChangeEvent, type DragEvent } from 'react'
import { Upload, FileText, AlertCircle, CheckCircle2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/auth'

const ALLOWED_MIME = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
]

const MAX_BYTES = 10 * 1024 * 1024 // 10 MB

export function DocumentUpload({
  caseId,
  onUploaded,
}: {
  caseId: string
  onUploaded?: () => void
}) {
  const { user } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  async function handleFile(file: File) {
    setError(null)
    setSuccess(null)
    if (!user) {
      setError('You must be signed in.')
      return
    }
    if (!ALLOWED_MIME.includes(file.type)) {
      setError(`Unsupported file type: ${file.type || 'unknown'}.`)
      return
    }
    if (file.size > MAX_BYTES) {
      setError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB. Max is 10 MB.`)
      return
    }
    setBusy(true)
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
    const path = `${user.id}/${caseId}/${Date.now()}-${safeName}`
    let storageObjectCreated = false

    try {
      const { error: upErr } = await supabase.storage
        .from('intake-documents')
        .upload(path, file, { contentType: file.type, upsert: false })
      if (upErr) throw upErr
      storageObjectCreated = true

      const { error: insErr } = await supabase.from('intake_documents').insert({
        case_id: caseId,
        storage_path: path,
        original_filename: file.name,
        mime_type: file.type,
        size_bytes: file.size,
        ocr_status: 'pending',
      })
      if (insErr) throw insErr

      // Audit event.
      await supabase.from('case_events').insert({
        case_id: caseId,
        actor_id: user.id,
        kind: 'document.uploaded',
        payload: {
          filename: file.name,
          size_bytes: file.size,
          actor_label: user.email ?? 'Current user',
        },
      })

      setSuccess(`Uploaded ${file.name}. AI workflow processing…`)
      onUploaded?.()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg)
      // If the object made it into storage but we never wrote the
      // intake_documents row, the upload is orphaned. Remove the file
      // so the bucket doesn't accumulate untracked objects.
      if (storageObjectCreated) {
        const { error: rmErr } = await supabase.storage
          .from('intake-documents')
          .remove([path])
        if (rmErr) {
          console.warn('[vice] failed to clean up orphaned upload:', rmErr.message)
        }
      }
    } finally {
      setBusy(false)
    }
  }

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) void handleFile(file)
    e.target.value = ''
  }
  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) void handleFile(file)
  }

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative rounded-lg border-2 border-dashed p-8 text-center transition ${
          dragOver
            ? 'border-accent-sunset bg-accent-sunset/5'
            : 'border-neutral-300 bg-neutral-50 hover:border-secondary'
        }`}
      >
        <Upload size={28} aria-hidden="true" className="mx-auto text-secondary" />
        <p className="mt-3 text-sm font-semibold text-primary">
          Drop a document, or{' '}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="text-secondary underline decoration-accent-turquoise underline-offset-4 hover:text-primary disabled:opacity-50"
          >
            choose a file
          </button>
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          PDF, PNG, JPG, WebP, CSV, XLSX, or plain text. Max 10 MB.
        </p>
        {busy && (
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-neutral-600">
            <span className="w-3 h-3 rounded-full border-2 border-accent-sunset border-t-transparent animate-spin" />
            Uploading…
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          onChange={onPick}
          accept={ALLOWED_MIME.join(',')}
          aria-label="Choose a document to upload"
        />
      </div>

      {error && (
        <div role="alert" className="flex items-start gap-2 text-sm text-error bg-error/5 border border-error/20 rounded p-3">
          <AlertCircle size={16} aria-hidden="true" className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-2 text-sm text-success bg-success/5 border border-success/20 rounded p-3">
          <CheckCircle2 size={16} aria-hidden="true" className="flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      <p className="text-[11px] text-neutral-500 flex items-center gap-1">
        <FileText size={12} aria-hidden="true" />
        Files are uploaded to a private bucket scoped to your account.
      </p>
    </div>
  )
}
